import { tz, TZDate } from '@date-fns/tz'

import {
  parse,
  format,
  Interval,
  isWithinInterval,
  parseISO,
  isValid,
  addSeconds,
  differenceInMilliseconds,
  differenceInSeconds,
  transpose
} from 'date-fns'

import log from './logger'

const timeToDuration = (time: string): number => {
  return String(time)
    .split(':')
    .map(Number)
    .reverse()
    .reduce((acc, val, idx) => acc + val * Math.pow(60, idx), 0)
}

// Migrate from date-fns-tz to date-fns v4 and @date-fns/tz
const toZonedTime = (date: Date, timezone: string) => new TZDate(date, timezone)
const fromZonedTime = (date: Date, timezone?: string) => {
  return timezone ? transpose(date, tz(timezone)) : new TZDate(date)
}

const TIME_ZONE_MAP = Object.freeze({
  'ET': 'America/New_York', // Eastern Time (General)
  'EST': 'America/New_York', // Eastern Standard Time
  'EDT': 'America/New_York', // Eastern Daylight Time
  'CT': 'America/Chicago', // Central Time
  'MT': 'America/Denver', // Mountain Time
  'PT': 'America/Los_Angeles', // Pacific Time
  'PST': 'America/Los_Angeles', // Pacific Standard Time
  'PDT': 'America/Los_Angeles', // Pacific Daylight Time
  'AKST': 'America/Anchorage', // Alaska Standard Time
  'AKDT': 'America/Anchorage', // Alaska Daylight Time
  'HST': 'Pacific/Honolulu', // Hawaii Standard Time (no DST)
  'SST': 'Pacific/Pago_Pago', // Samoa Standard Time
  'AST': 'America/Puerto_Rico', // Atlantic Standard Time
  'GMT': 'Etc/GMT', // Greenwich Mean Time
  'UTC': 'Etc/UTC', // Coordinated Universal Time
  'CET': 'Europe/Paris', // Central European Time
  'EET': 'Europe/Helsinki', // Eastern European Time
  'IST': 'Asia/Kolkata', // Indian Standard Time
  'CST': 'Asia/Shanghai', // China Standard Time
  'JST': 'Asia/Tokyo', // Japan Standard Time
  'AEST': 'Australia/Sydney', // Australian Eastern Standard Time
  'AEDT': 'Australia/Sydney' // Australian Eastern Daylight Time
})

function toTimezone(key: string, fallback?: string) {
  return TIME_ZONE_MAP[key as never] || fallback || TIME_ZONE_MAP['ET']
}

/**
 * Check if date of event is in range
 * @param startDateTime
 * @param endDateTime
 * @param timezone
 * @returns { boolean }
 */
function dateInRange(startDateTime: string | null, endDateTime: string | null, timezone: keyof typeof TIME_ZONE_MAP | null): boolean {
  if (!startDateTime || !endDateTime || !timezone) return false
  try {
    const tz = TIME_ZONE_MAP[timezone]
    const currentDate = new Date()

    const dateFormats = ['M/d/yyyy h:mm aa', 'yyyy-MM-dd HH:mm:ss']
    const dates = [startDateTime, endDateTime].map(dateTime => {
      const [date] = [...dateFormats.map(format => parse(dateTime, format, currentDate)), parseISO(dateTime)]
        .filter(isValid)
      return date
    })

    if (!dates.every(isValid)) return false

    const [parsedStartDate, parsedEndDate] = dates
    const zonedCurrentDate = toZonedTime(currentDate, tz)

    return isWithinInterval(zonedCurrentDate, {
      start: parsedStartDate,
      end: parsedEndDate
    })
  } catch {
    log.ERROR('Incorrect dates', {
      startDateTime,
      endDateTime
    })
    return false
  }
}

function dateToUTCStamp(date: string, format: string): number {
  if (!date) return 0
  const d = parse(date, format, new Date)
  const offset = d.getTimezoneOffset() * 60

  return (d.getTime() / 1000) - offset
}

/**
 * Format date by template
 * @param value
 * @param template
 * @param timezone
 * @returns
 */
const formatDate = (value?: string | Date | null | undefined, template = 'LLL d, y', timezone: string | null = '') => {
  if (!value) return ''
  const date = value instanceof Date ? value : new Date(value)
  return `${format(date, template)} ${timezone}`.trim()
}

function getHourAbbreviation(hours: number): string {
  return hours === 1 ? 'hr' : 'hrs'
}

/**
 * Format time in seconds to human readable string
 * @param value time in seconds
 * @param short show only hours
 * @returns
 */
function formatToHumanTime(value: number, short = false) {
  if (typeof value !== 'number') return '0 min'

  const hours = Math.floor(value / 3600)
  const minutes = Math.floor(value / 60) % 60

  if (short && hours > 100) return `${hours} ${getHourAbbreviation(hours)}`
  else if (hours > 0 && minutes > 0) {
    return `${hours} ${getHourAbbreviation(hours)} ${minutes} min`
  } else if (hours > 0 && minutes === 0) {
    return `${hours} ${getHourAbbreviation(hours)}`
  } else return `${minutes} min`
}


/**
 * Format time in seconds to string 'HH?:MM:SS'
 * @param time - time in seconds
 * @param leadingZero - add leading zero like '0h:0m:0s'
 */
const formatTime = (time: number, leadingZero = true) => {
  time = Number(time)
  if (time >= 0) {
    const totalSeconds = Math.round(time)
    const hours = Math.floor(totalSeconds / 3600)
    const remainingSeconds = totalSeconds % 3600
    const minutes = Math.floor(remainingSeconds / 60) % 60
    const seconds = Math.round(remainingSeconds % 60)

    return [hours, minutes, seconds]
      .filter((v, i) => i > 0 ? true : v > 0)
      .map((v, i) => {
        const l = String(v).length === 1
        if (i === 0 && l) {
          return leadingZero ? `0${v}` : v
        } else if (l) {
          return `0${v}`
        }
        return v
      }).join(':')
  }

  return leadingZero ? '00:00' : '0:00'
}

function timestamp() {
  return new Date().getTime()
}

const isValidDate = (date: Date): boolean => date instanceof Date && !isNaN(date.getTime())
const intervalsToDuration = (intervals: Interval[]) =>
  intervals.reduce((acc, curr) => acc + (+curr.end - +curr.start), 0)


function getNonOverlappingIntervals(intervals: Array<Interval>) {
  if (intervals.length <= 1) return intervals

  const sortedIntervals = [...intervals].sort((a, b) => Number(a.start) - Number(b.start))
  const result = new Array<Interval>()

  let newInterval = { start: sortedIntervals[0].start, end: sortedIntervals[0].end }

  result.push(newInterval)

  for (let i = 0; i < sortedIntervals.length; i++) {
    if (sortedIntervals[i].start <= newInterval.end)
      newInterval.end = Math.max(Number(newInterval.end), Number(sortedIntervals[i].end))
    else {
      newInterval = sortedIntervals[i]
      result.push(newInterval)
    }
  }

  return result
}

/**
 * Calculate time difference between current time and expiration date based on expiration time
 * @param dateAt { string | Date } - date in format 'M/d/yyyy h:mm:ss a'
 * @param expiresIn { number } - time in seconds
 * @returns - difference in milliseconds
 */
function diffInSecondsByExpirationTimeUTC(dateAt: string | Date, expiresIn: number) {
  const expiresAtDate = addSeconds(new Date(dateAt), expiresIn)
  const currentDate = new Date()
  return Math.max(differenceInMilliseconds(expiresAtDate, currentDate), 0)
}

/**
 * Calculate time difference between current time and expiration date based on expiration time
 * @param dateAt { string | Date } - date in format 'M/d/yyyy h:mm:ss a'
 * @param expiresIn { number } - time in seconds
 * @returns - difference in milliseconds
 */
function diffInSecondsByExpirationTime(dateAt: string | Date, expiresIn: number, format = 'M/d/yyyy h:mm:ss a') {
  const expiresAtDate = addSeconds(parse(String(dateAt), format, new Date), expiresIn)
  const currentDate = toZonedTime(new Date, toTimezone('PT'))
  return Math.max(differenceInMilliseconds(expiresAtDate, currentDate), 0)
}

/**
 * Calculate difference in seconds between current local time and future event date
 * @param dateAt { string | Date } - date in format 'M/d/yyyy h:mm:ss a'
 * @returns - difference in seconds
 */
function diffInSecondsUntilDate(dateAt?: Nullable<string | Date>, timezone = 'ET') {
  if (!dateAt) return NaN
  const currentDate = toZonedTime(new Date, toTimezone(timezone))
  return Math.max(differenceInSeconds(parseISO(String(dateAt)), currentDate), 0)
}

/**
 * Convert date to local date
  * @param date - date in format 'M/d/yyyy h:mm:ss a'
  * @param timezone - timezone
  * @param template - template for date format (default: 'LLL d, y, h:mm a').
  * https://date-fns.org/v2.30.0/docs/format
 */
export function toLocalDate(date?: Nullable<string | Date>, timezone?: Nullable<string>, template = 'LLL d, y, h:mm a') {
  if (!date) return null
  return format(fromZonedTime(new Date(String(date)), toTimezone(timezone || 'ET')), template)
}

export {
  intervalsToDuration,
  getNonOverlappingIntervals,
  isValidDate,
  timeToDuration,
  diffInSecondsByExpirationTime,
  diffInSecondsByExpirationTimeUTC,
  diffInSecondsUntilDate,
  dateInRange,
  dateToUTCStamp,
  formatDate,
  formatTime,
  formatToHumanTime,
  timestamp,
  toTimezone,
  toZonedTime,
  fromZonedTime,
  TIME_ZONE_MAP
}

export type { Interval }
